import React, { useCallback, useState } from 'react';

import { Box, Text } from '@rbilabs/universal-components';
import { isNumber } from 'lodash-es';
import { LayoutChangeEvent, Pressable, StyleSheet, View } from 'react-native';

import Picture from 'components/picture';
import { primitive } from 'styles/constants/primitives';
import { EventName, emitEvent } from 'utils/event-hub';

import { CTAButtons } from '../components/cta-button';
import { ISimpleHomePageProps } from '../types';
import { sendHeroClickEvent } from '../utils';

const rgbColorMidpoint = Number('0x777777');

const _SimpleHomePageMobile = ({
  navigate,
  numberOfItemsInCart,
  serviceMode,
  item,
  flatListDimensions,
}: ISimpleHomePageProps) => {
  const [safeAreaHeight, setSafeAreaHeight] = useState<Number | null>(null);

  const {
    mobileImage,
    altImageText,
    _id,
    imageLink,
    mobileImageBackgroundColor,
    imageDisclaimerText,
    daypart,
  } = item;

  const imageAlt = altImageText?.locale ?? '';
  const image = mobileImage?.locale;

  const onImageLoaded = useCallback(() => {
    emitEvent(EventName.MARKETING_IMAGE_LOADED);
  }, []);

  // reverse the disclaimer color to white-ish if the background color is dark (below 50% of RGB space)
  const reverseDisclaimerColor =
    mobileImageBackgroundColor &&
    imageDisclaimerText?.locale &&
    Number('0x' + mobileImageBackgroundColor.replace('#', '')) < rgbColorMidpoint;

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { width, height } = event.nativeEvent.layout;
      // this sets a max height if the safe area of the image is going to get cut off horizontally
      // (this is extremely rare only with devices that are very tall and narrow)
      if (
        width &&
        isNumber(width) &&
        height &&
        isNumber(height) &&
        safeAreaHeight === null &&
        width / height < 0.625
      ) {
        // the aspect ratio of the image safe area is 1.5x the width
        setSafeAreaHeight(width * 1.5);
      }
    },
    [safeAreaHeight]
  );

  const handleOnPress = () => {
    if (imageLink) {
      sendHeroClickEvent({ imageLink, daypart });

      navigate(imageLink);
    }
  };

  return (
    <View testID="simple-home-page" style={{ height: flatListDimensions?.height }}>
      <Pressable
        style={[styles.pressable, { backgroundColor: mobileImageBackgroundColor || undefined }]}
        onPress={handleOnPress}
      >
        <Picture
          priority="high"
          image={image}
          alt={imageAlt}
          onImageLoaded={onImageLoaded}
          style={[styles.image, safeAreaHeight ? { maxHeight: safeAreaHeight as any } : undefined]}
          onLayout={onLayout}
        />
        {imageDisclaimerText?.locale && (
          <DisclaimerText
            style={reverseDisclaimerColor ? { color: primitive.bk.$mayoEggWhite } : undefined}
          >
            {imageDisclaimerText?.locale}
          </DisclaimerText>
        )}
      </Pressable>
      <FooterContainer>
        <CTAButtons
          numberOfItemsInCart={numberOfItemsInCart}
          serviceMode={serviceMode}
          navigate={navigate}
          itemId={_id}
        />
      </FooterContainer>
    </View>
  );
};

const styles = StyleSheet.create({
  pressable: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    flex: 1,
  },
});

const DisclaimerText = Text.withConfig({
  fontSize: '9px',
  marginBottom: '$2',
  marginLeft: '$5',
  alignSelf: 'flex-start',
});

const FooterContainer = Box.withConfig({
  paddingX: '$4',
  paddingY: '$4',
});

export const SimpleHomePageMobile = React.memo(_SimpleHomePageMobile);
